'use client';
import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { HeroBlock, MovieDetails } from '@intigral/shared';
import styles from './videopopup.module.sass';
import cn from 'classnames';
import { MovieButtons } from '../MovieButtons';
import { useMediaQuery, useTimeout } from '@intigral/hooks';

interface VideoPopupProps
    extends React.ComponentProps<typeof MovieButtons>,
        React.ComponentProps<typeof MovieDetails>,
        React.ComponentProps<typeof HeroBlock>,
        React.HTMLAttributes<HTMLDivElement> {
    cover: string;
    setIsActive?: React.Dispatch<React.SetStateAction<boolean>>;
    children?: React.ReactElement;
}

export const VideoPopup = forwardRef(({ cover, setIsActive, children, ...props }: VideoPopupProps, ref) => {
    const {
        play,
        progress,
        watchlist,
        watchlistButton,
        page,
        trailer,
        download,
        reminderButton,
        provider,
        locked,
        dolby,
        quality4K,
        onClickLock,
        rating,
        info,
        live,
        picture,
        heading,
        compact,
        minHeight,
        detail,
        theme,
        blurred,
        className,
        ...restDivProps
    } = props;

    const movieButtonsProps = {
        play,
        progress,
        watchlist,
        watchlistButton,
        page,
        trailer,
        download,
        reminderButton,
    };

    const movieDetailsProps = {
        provider,
        locked,
        dolby,
        quality4K,
        onClickLock,
        rating,
        info,
        live,
    };

    const heroBlockProps = {
        picture,
        heading,
        compact,
        minHeight,
        detail,
        theme,
        blurred,
    };

    const cardRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const [showPopup, setShowPopup] = useState(false);
    const [hovered, setHovered] = useState(false);

    const { start: startShowTimeout, clear: clearShowTimeout } = useTimeout(() => setShowPopup(true), 450);
    const { start: startHideTimeout, clear: clearHideTimeout } = useTimeout(() => setShowPopup(false), 300);

    const positionPopup = useCallback(() => {
        if (!cardRef.current || !popupRef.current) {
            // console.log('Refs are not attached');
            return;
        }

        const cardRect = cardRef.current.getBoundingClientRect();
        const popupRect = popupRef.current.getBoundingClientRect();

        const popupInitialX = cardRect.left + cardRect.width / 2 - popupRect.width / 2;
        const popupInitialY = cardRect.top + cardRect.height / 2 - popupRect.height / 2;

        const adjustedLeft = Math.min(Math.max(popupInitialX, 0), window.innerWidth - popupRect.width);
        const adjustedTop = Math.min(Math.max(popupInitialY, 0), window.innerHeight - popupRect.height);

        popupRef.current.style.left = `${adjustedLeft}px`;
        popupRef.current.style.top = `${adjustedTop}px`;
        // console.log('Popup positioned at:', adjustedLeft, adjustedTop);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setShowPopup(false);
        };

        window.addEventListener('scroll', handleScroll);

        if (hovered) {
            clearHideTimeout();
            startShowTimeout();
            positionPopup();
        } else {
            clearShowTimeout();
            startHideTimeout();
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearShowTimeout();
            clearHideTimeout();
        };
    }, [hovered, startShowTimeout, startHideTimeout, clearShowTimeout, clearHideTimeout, positionPopup]);

    const handleMouseEnter = () => {
        setHovered(true);
        if (setIsActive) setIsActive(true);
    };

    const handleMouseLeave = () => {
        // console.log('mouse');
        setHovered(false);
        if (setIsActive) setIsActive(false);
    };

    useImperativeHandle(ref, () => ({
        showPopup: () => setShowPopup(true),
        hidePopup: () => setShowPopup(false),
    }));

    const isMobile = useMediaQuery('(max-width: 924px)');

    const popupContent = (
        <div
            ref={popupRef}
            className={cn(styles.popup, { [styles.fadeIn]: showPopup, [styles.fadeOut]: !showPopup }, className)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <HeroBlock {...heroBlockProps} compact>
                {isMobile && <MovieButtons {...movieButtonsProps} className={styles.trailer} />}
            </HeroBlock>
            <div className={styles.foot}>
                <MovieButtons {...movieButtonsProps} />
                <MovieDetails {...movieDetailsProps} />
            </div>
        </div>
    );

    return (
        <div
            ref={cardRef}
            className={className}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...(isMobile && page?.onClick
                ? {
                      onClick: (event) =>
                          page?.onClick?.(event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>),
                  }
                : {})}
            {...restDivProps}
        >
            {children}

            {!isMobile && (showPopup || hovered) && (ReactDOM.createPortal(popupContent, document.body) as ReactNode)}
        </div>
    );
});
